import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    FolderOpen, MapPin, Calendar, User, Wrench, Mail, FileText,
    MessageSquare, ChevronRight, ChevronDown, ChevronUp, Clock,
    CheckCircle2, Circle, Loader2, X, Search, Plus, Edit3,
    Download, Send, Navigation, Trash2, AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ActivityFeed from "@/components/ActivityFeed";
import LogisticsSection from "@/components/operations/LogisticsSection";
import SiteSurveySection from "@/components/operations/SiteSurveySection";
import MySystemSection from "@/components/operations/MySystemSection";
import StageSummarySection from "@/components/operations/StageSummarySection";
import StageHistorySection from "@/components/operations/StageHistorySection";
import ProjectAttributionSection from "@/components/operations/ProjectAttributionSection";
import SalesActionsSection from "@/components/sales/SalesActionsSection";
import OpportunityOutcomeSection from "@/components/sales/OpportunityOutcomeSection";
import SalesStageSection from "@/components/sales/SalesStageSection";
import CalendarAppointmentForm from "@/components/calendar/CalendarAppointmentForm";
import ClashWarningDialog from "@/components/calendar/ClashWarningDialog";
import AvailabilityWarningDialog from "@/components/calendar/AvailabilityWarningDialog";
import TaskAssigneesDialog from "@/components/operations/TaskAssigneesDialog";
import TaskReopenDialog from "@/components/operations/TaskReopenDialog";
import { openDirections, resolveMapAddress } from "@/lib/directions";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * DEV-003A — Interactive Project detail for /operations/projects.
 *
 * Handles:
 *  • Collapsible Phases panel (default expanded)
 *  • Clickable phase rows → inline task drill-down
 *  • Clickable tasks → inline status update via existing
 *    `PATCH /projects/{pid}/tasks/{tid}` endpoint
 *  • Editable status pill (uses `updateProjectState`)
 *  • Assign / clear lead engineer (uses `assignLeadEngineer`)
 *  • Docs / Messages / Next Visit tiles open scoped side dialogs that
 *    reuse the existing endpoints — no navigation away from Operations.
 *
 * Emits DEV-002 Activity Engine events implicitly because every mutation
 * routes through the existing installer endpoints, which already record
 * activity for engineer assignment, status changes and milestone changes.
 */

import { projectCompletionValue, projectCompletionField, projectNextMilestone, projectStageLabel } from "@/lib/projectPresentation";
import ProjectSummaryCard from "@/components/operations/ProjectSummaryCard";

const STATE_LABEL = { active: "Active", on_hold: "On hold", completed: "Completed" };
const STATE_DOT = { active: "bg-[#3E9E2E]", on_hold: "bg-[#F5A524]", completed: "bg-[#4B5565]" };
const STATE_OPTIONS = [
    { key: "active", label: "Active" },
    { key: "on_hold", label: "On hold" },
    { key: "completed", label: "Completed" },
];

const TASK_STATUS_LABEL = {
    not_started: "Not started",
    in_progress: "In progress",
    complete: "Complete",
};
const TASK_STATUS_NEXT = {
    not_started: "in_progress",
    in_progress: "complete",
    complete: "not_started",
};

function formatDate(iso, opts) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, opts || { day: "numeric", month: "short", year: "numeric" });
    } catch { return "—"; }
}

export default function OperationsProjectDetail({ project, onProjectUpdated, reviewSurveyId, focusTaskId = null, focusStage = false }) {
    const { user } = useAuth();
    // Only Security Admins can permanently delete projects. This gates the
    // UI to match the existing backend `require_security_admin` guard on
    // `POST /admin/projects/bulk-delete` — never invent a new capability.
    const canDeleteProject = !!user?.is_security_admin;
    // FIELD-001B · Operations task-assignee picker is gated on the same
    // `manage_projects` permission that guards the backend endpoint.
    const canManageProjects = Array.isArray(user?.permissions)
        && user.permissions.includes("manage_projects");
    // DEV-019/020 · The shared Project Focus is mounted for both
    // Operations (`/operations/projects/:id`) and Sales
    // (`/sales/projects/:id`). Sales users may see the Project Lead
    // for context but MUST NOT be presented with any assign/change/
    // clear affordance — the backend enforces this, but the UI must
    // never invite a mutation that will be rejected.
    const isSales = (user?.role || "").toLowerCase() === "sales";
    const canManageProjectLead = !isSales && canManageProjects;
    const [assigneesDialog, setAssigneesDialog] = useState(null);
    // FIELD-001C2A · Ops-only canonical task reopen dialog state.
    const [reopenDialog, setReopenDialog] = useState(null);
    // shape: { task, currentAssigneeIds }
    const [engineersById, setEngineersById] = useState({});
    useEffect(() => {
        (async () => {
            try {
                // FIELD-RESOURCE-ASSIGNMENT-CORRECTION · Include archived
                // rows so stale assignee chips resolve to
                // "James Carter (archived)" instead of leaking the raw
                // UUID. The picker (TaskAssigneesDialog) still limits
                // selectable options to active resources — this map is
                // used ONLY for display-name lookup on the row chip.
                const rows = await api.listEngineers({ includeArchived: true });
                const map = {};
                for (const e of (rows || [])) map[e.id] = e;
                setEngineersById(map);
            } catch { /* best-effort — display name fallbacks to id */ }
        })();
    }, []);
    // DEV-010H post-UAT · Global "collapsed by default" convention.
    // Phases collapse on entry unless a deep-link explicitly requests
    // an expanded target (e.g. `?openPhase=<id>` from a notification).
    const [phasesCollapsed, setPhasesCollapsed] = useState(true);
    const [openPhases, setOpenPhases] = useState(() => new Set());
    const [savingTask, setSavingTask] = useState(null);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [savingState, setSavingState] = useState(false);
    // Delete-project state
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);

    // Modal state
    const [engineerModal, setEngineerModal] = useState(false);
    const [visitModal, setVisitModal] = useState(false);
    const [docsModal, setDocsModal] = useState(false);
    const [messagesModal, setMessagesModal] = useState(false);

    // CAL-001A · Inline appointment scheduling for Operations users.
    // Reuses the SAME `CalendarAppointmentForm` + `ClashWarningDialog`
    // components the Installer Calendar uses, so validation, clash
    // detection and the underlying `POST /api/calendar/appointments`
    // contract are shared. Operations never traverses `/installer/*`.
    const [scheduleFormOpen, setScheduleFormOpen] = useState(false);
    const [scheduleAssets, setScheduleAssets] = useState({
        engineers: [], types: [], loading: false, error: "",
    });
    const [scheduleClash, setScheduleClash] = useState({
        open: false, clashes: [], onResume: null,
    });
    const [scheduleAvail, setScheduleAvail] = useState({
        open: false, warnings: [], onResume: null,
    });

    // Reset local UI state when switching selected project
    useEffect(() => {
        setOpenPhases(new Set());
        // DEV-010H post-UAT · Collapsed on new project entry unless the
        // caller deep-linked to a specific phase via `?openPhase=`.
        const targetPhase = new URLSearchParams(window.location.search).get("openPhase");
        setPhasesCollapsed(!targetPhase);
        if (targetPhase) setOpenPhases(new Set([targetPhase]));
        setStatusMenuOpen(false);
    }, [project?.id]);

    // OPS-004B3 · Always fetch the canonical detail on selection /
    // route change. The list endpoints (`/projects/summary` and
    // `/operations/projects/query`) return a LIGHTWEIGHT row —
    // it doesn't carry `phases`, `stage_history`, `email`,
    // `estimated_completion`, `next_milestone`, `current_owner`.
    // Without this fetch, reopening any active / on-hold /
    // completed project would render the Phases/Tasks section
    // empty and the header would drop customer email / estimates.
    // Completed projects MUST still show every historical phase
    // and task as read-only. Hard refresh + navigation both hit
    // this effect because `project.id` changes.
    useEffect(() => {
        if (!project?.id) return;
        // Skip if the object we already hold looks like a full
        // detail record (has phases OR ordered stage_history). The
        // detail endpoint always returns `phases` — the list
        // endpoints never do.
        const isFullDetail =
            Array.isArray(project.phases) && project.phases.length > 0
            && Array.isArray(project.stage_history);
        if (isFullDetail) return;
        let cancelled = false;
        (async () => {
            try {
                const fresh = await api.getProject(project.id);
                if (cancelled) return;
                // Merge onto the current row: keep list-only
                // annotations (`unread_customer_messages`,
                // `outstanding_tasks`, `is_overdue`) that the
                // detail response doesn't carry, but let every
                // detail field win.
                onProjectUpdated({ ...project, ...fresh });
            } catch (e) {
                // Fail visibly — silent partial data misleads UAT.
                toast.error(formatApiError(e?.response?.data?.detail)
                    || "Could not load project detail");
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id]);

    // OPS-003B2 · Deep-link resolution for a specific overdue task.
    // Locates the phase containing `focusTaskId`, expands it, scrolls
    // the task into view and briefly highlights it. Never falls back
    // to the dashboard — an invalid id surfaces a clear banner below.
    const focusResult = useMemo(() => {
        if (!focusTaskId) return null;
        for (const ph of (project?.phases || [])) {
            for (const t of (ph.tasks || [])) {
                if (t.id === focusTaskId) return { phase: ph, task: t };
            }
        }
        return { phase: null, task: null, missing: true };
    }, [focusTaskId, project?.phases]);

    useEffect(() => {
        if (!focusResult?.phase) return;
        // Auto-expand the containing phase.
        setOpenPhases((prev) => {
            const next = new Set(prev);
            next.add(focusResult.phase.id);
            return next;
        });
        setPhasesCollapsed(false);
        // Scroll + highlight once the DOM has expanded.
        const t = setTimeout(() => {
            const el = document.querySelector(
                `[data-testid="ops-projects-detail-task-${focusResult.task.id}"]`,
            );
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.setAttribute("data-focused", "true");
                setTimeout(() => el.removeAttribute("data-focused"), 3200);
            }
        }, 220);
        return () => clearTimeout(t);
    }, [focusResult, project?.id]);

    // OPS-DASHBOARD-DEEP-LINKS · Derive the canonical current-stage
    // task from stable metadata. Prefer a backend-supplied stable
    // id if present; otherwise match the first non-complete task in
    // the CURRENT phase whose display text equals
    // `expected_next_action` / `next_milestone`. Matching is scoped
    // to the current project and phase only — never guessed across
    // projects.
    const currentStageFocus = useMemo(() => {
        if (!project) return null;
        const phases = project.phases || [];
        // Pick the phase for the project's current_stage. Match by
        // stage key, then by name, then fall back to the first
        // non-terminal phase.
        const stageKey = project.current_stage;
        let phase = null;
        if (stageKey) {
            phase = phases.find(
                (p) => (p.stage_key === stageKey) || (p.key === stageKey)
                        || (p.name && p.name.toLowerCase() === String(project.stage_label || "").toLowerCase()),
            ) || null;
        }
        if (!phase) return null;
        const stableId = project.expected_next_task_id
            || project.current_stage_next_task_id
            || null;
        let task = null;
        if (stableId) {
            task = (phase.tasks || []).find((t) => t.id === stableId) || null;
        }
        if (!task) {
            const label = (project.expected_next_action || project.next_milestone || "").trim();
            if (label) {
                task = (phase.tasks || []).find(
                    (t) => (t.name || "").trim() === label
                           && t.status !== "complete"
                           && t.status !== "cancelled",
                ) || null;
            }
        }
        return { phase, task, label: project.expected_next_action || project.next_milestone || "" };
    }, [project]);

    // OPS-DASHBOARD-DEEP-LINKS · When the parent asked us to focus
    // the current stage, expand the containing phase and (if a
    // canonical task was matched) reuse the existing focus-task
    // scroll/highlight flow.
    useEffect(() => {
        if (!focusStage || !currentStageFocus?.phase) return;
        setOpenPhases((prev) => {
            const next = new Set(prev);
            next.add(currentStageFocus.phase.id || currentStageFocus.phase.name);
            return next;
        });
        setPhasesCollapsed(false);
        if (currentStageFocus.task) {
            const t = setTimeout(() => {
                const el = document.querySelector(
                    `[data-testid="ops-projects-detail-task-${currentStageFocus.task.id}"]`,
                );
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    el.setAttribute("data-focused", "true");
                    setTimeout(() => el.removeAttribute("data-focused"), 3200);
                }
            }, 220);
            return () => clearTimeout(t);
        }
    }, [focusStage, currentStageFocus, project?.id]);

    // Close state menu on Escape
    useEffect(() => {
        if (!statusMenuOpen) return;
        const onKey = (e) => e.key === "Escape" && setStatusMenuOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [statusMenuOpen]);

    const togglePhase = (phaseId) => {
        setOpenPhases((prev) => {
            const next = new Set(prev);
            next.has(phaseId) ? next.delete(phaseId) : next.add(phaseId);
            return next;
        });
    };

    const refreshProject = useCallback(async () => {
        try {
            const fresh = await api.getProject(project.id);
            // Enrich with fields only listProjectsSummary carries. We
            // merge so the summary-only extras (reference, postcode,
            // outstanding_tasks, unread_customer_messages…) survive.
            onProjectUpdated({ ...project, ...fresh });
        } catch (e) {
            /* toast handled by callers */
        }
    }, [project, onProjectUpdated]);

    // DEV-003B: apply an explicit patch to the selected project. Used
    // when a mutation already returned authoritative fields and we
    // don't need to hit `getProject` again — avoids stale summary
    // fields (like `assigned_engineer`) sticking around after the
    // truth has changed.
    const patchProject = useCallback((patch) => {
        onProjectUpdated({ ...project, ...patch });
    }, [project, onProjectUpdated]);

    // ─── Mutations ─────────────────────────────────────────────────

    // WF-001A1A · In-app operational-override dialog. Replaces the
    // native browser prompt (which throws in the preview shell).
    // The dialog is fully driven by the structured refusal payload
    // returned by the API — no prose parsing anywhere.
    const [overrideDialog, setOverrideDialog] = useState(null);
    // shape: { task, evaluation, message, reason, submitting, error, originTestId }

    const handleTaskClick = async (task) => {
        if (savingTask) return;
        // FIELD-001C · Completed rows are non-mutating. Operations must
        // use the explicit Reopen action (mandatory reason + audit) to
        // move a task back to `in_progress`. The status circle no
        // longer acts as a cycle control on completed rows.
        if (task.status === "complete") {
            toast.info("Completed — use Reopen (a reason is required) to change this task.");
            return;
        }
        setSavingTask(task.id);
        try {
            const next = TASK_STATUS_NEXT[task.status] || "in_progress";
            await api.updatePhaseTaskStatus(project.id, task.id, next);
            toast.success(`Task marked ${TASK_STATUS_LABEL[next].toLowerCase()}`);
            await refreshProject();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code === "TASK_DEPENDENCY_UNMET") {
                const ev = detail.evaluation || {};
                const msg = detail.message || "This task cannot be completed yet.";
                if (ev.severity === "operational") {
                    const canOverride = (user?.role === "operations")
                        || Boolean(user?.is_security_admin);
                    if (!canOverride) {
                        toast.error(msg);
                    } else {
                        setOverrideDialog({
                            task,
                            evaluation: ev,
                            message: msg,
                            reason: "",
                            submitting: false,
                            error: "",
                            originTestId: `ops-projects-detail-task-toggle-${task.id}`,
                        });
                    }
                } else {
                    // HARD gate — never opens the override dialog.
                    toast.error(msg);
                }
            } else {
                toast.error(formatApiError(detail) || "Could not update task");
            }
        } finally {
            setSavingTask(null);
        }
    };

    const closeOverrideDialog = useCallback(() => {
        // WF-001A1A · Return focus to the originating task control.
        const originTestId = overrideDialog?.originTestId;
        setOverrideDialog(null);
        if (originTestId) {
            // Defer so the dialog unmounts before we reach for the trigger.
            setTimeout(() => {
                const el = document.querySelector(`[data-testid="${originTestId}"]`);
                if (el && typeof el.focus === "function") el.focus();
            }, 0);
        }
    }, [overrideDialog]);

    const confirmOverride = useCallback(async () => {
        if (!overrideDialog) return;
        const reason = (overrideDialog.reason || "").trim();
        if (!reason || overrideDialog.submitting) return;
        setOverrideDialog((s) => ({ ...s, submitting: true, error: "" }));
        try {
            const t = overrideDialog.task;
            const next = TASK_STATUS_NEXT[t.status] || "in_progress";
            await api.updatePhaseTaskStatus(project.id, t.id, next,
                { overrideReason: reason });
            toast.success("Task marked complete (override recorded)");
            const originTestId = overrideDialog.originTestId;
            setOverrideDialog(null);
            await refreshProject();
            if (originTestId) {
                setTimeout(() => {
                    const el = document.querySelector(`[data-testid="${originTestId}"]`);
                    if (el && typeof el.focus === "function") el.focus();
                }, 0);
            }
        } catch (err) {
            const d = err?.response?.data?.detail;
            const errMsg = (typeof d === "string" ? d
                : d?.message || formatApiError(d)) || "Override refused";
            setOverrideDialog((s) => s
                ? { ...s, submitting: false, error: errMsg }
                : s);
        }
    }, [overrideDialog, project.id, refreshProject]);

    const handleStateChange = async (nextState) => {
        if (savingState || nextState === (project.state || "active")) {
            setStatusMenuOpen(false);
            return;
        }
        setSavingState(true);
        try {
            await api.updateProjectState(project.id, nextState);
            toast.success(`Project marked ${STATE_LABEL[nextState].toLowerCase()}`);
            await refreshProject();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not update status");
        } finally {
            setSavingState(false);
            setStatusMenuOpen(false);
        }
    };

    // UAT blocker fix · Operations delete-project. Reuses the existing
    // canonical cascade endpoint (`POST /admin/projects/bulk-delete`)
    // which archives documents and hard-deletes tasks, appointments,
    // notifications, messages, logistics, surveys, roof_elevations and
    // installation_specifications tied to the project. Gated by the
    // same `require_security_admin` permission the endpoint enforces.
    const handleDeleteProject = async () => {
        if (deleting) return;
        setDeleting(true);
        try {
            const result = await api.bulkDeleteProjects([project.id]);
            if (result?.deleted >= 1) {
                toast.success("Project permanently deleted");
                setDeleteOpen(false);
                // Signal the parent list to drop this row. The list page
                // watches `onProjectUpdated({ id, _deleted: true })` and
                // reloads if it doesn't recognise the marker.
                onProjectUpdated({ id: project.id, _deleted: true });
            } else {
                const reason = result?.failures?.[0]?.reason || "unknown_error";
                toast.error(`Delete failed: ${reason}`);
            }
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
        } finally {
            setDeleting(false);
            setDeleteConfirmText("");
        }
    };

    // CAL-001A1 · Stable references for the shared appointment form.
    // The form's reset effect fires on `open` and `initial.id`
    // changes, but memoising these props keeps ancestor SWR-driven
    // re-renders from thrashing anything downstream. Belt-and-braces.
    const scheduleInitial = useMemo(() => ({
        project_id: project.id,
        appointment_type: scheduleAssets.types[0] || "Site Survey",
        engineer_ids: [],
    }), [project.id, scheduleAssets.types]);

    const scheduleProjectsList = useMemo(() => ([{
        id: project.id,
        customer_name: project.customer_name,
        address: project.address,
        system_summary: project.system_summary,
    }]), [project.id, project.customer_name, project.address, project.system_summary]);

    const scheduleAppointmentTypes = useMemo(
        () => (scheduleAssets.types.length ? scheduleAssets.types : ["Site Survey"]),
        [scheduleAssets.types],
    );

    // CAL-001A · Ops "Schedule visit" — lazy-load engineers + types
    // and open the shared CalendarAppointmentForm pre-selected on
    // this project. Cancel returns to the project; save refreshes
    // it so `next_appointment` reflects the new booking.
    const openScheduleForm = useCallback(async () => {
        setVisitModal(false);
        setScheduleFormOpen(true);
        if (scheduleAssets.engineers.length && scheduleAssets.types.length) return;
        setScheduleAssets((s) => ({ ...s, loading: true, error: "" }));
        try {
            const [engs, ts] = await Promise.all([
                api.listEngineers({ includeArchived: true }),
                api.listAppointmentTypes(),
            ]);
            setScheduleAssets({
                engineers: engs || [],
                types: ts && ts.length ? ts : ["Site Survey"],
                loading: false,
                error: "",
            });
        } catch (e) {
            setScheduleAssets({
                engineers: [], types: ["Site Survey"], loading: false,
                error: formatApiError(e?.response?.data?.detail) || "Failed to load scheduling data",
            });
        }
    }, [scheduleAssets.engineers.length, scheduleAssets.types.length]);

    const closeScheduleForm = useCallback(async (saved) => {
        setScheduleFormOpen(false);
        if (saved) await refreshProject();
    }, [refreshProject]);

    const nextAppt = project.next_appointment;
    const state = project.state || "active";
    // DEV-003B: The tile represents the *lead* engineer for the
    // project (which the dialog assigns and clears). Anything derived
    // from appointments belongs on the Next Visit tile instead.
    const assignedEngineer = project.lead_engineer_name || null;

    return (
        <div className="p-4 md:p-6 space-y-4" data-testid="ops-projects-detail">
            {/* Header row with editable state pill + delete action */}
            <div className="flex items-start justify-between gap-4 -mt-1">
                <div className="min-w-0" />
                <div className="flex items-center gap-2">
                    {canDeleteProject && (
                        <button
                            type="button"
                            onClick={() => { setDeleteOpen(true); setDeleteConfirmText(""); }}
                            data-testid="ops-projects-detail-delete-btn"
                            title="Permanently delete this project and all related records"
                            className="inline-flex items-center gap-1.5 text-[11px] text-[#B91C1C] px-2.5 py-1 rounded-full bg-[#FEF2F2] hover:bg-[#FEE2E2] border border-[#FECACA] transition-colors"
                        >
                            <Trash2 size={11} strokeWidth={2} /> Delete project
                        </button>
                    )}
                    <div className="relative">
                    <button
                        type="button"
                        onClick={() => setStatusMenuOpen((v) => !v)}
                        disabled={savingState}
                        data-testid="ops-projects-detail-state-pill"
                        className="inline-flex items-center gap-1.5 text-[11px] text-[#131B2F] px-2.5 py-1 rounded-full bg-[#F5F5F7] hover:bg-[#EEEEF2] transition-colors"
                        aria-haspopup="menu"
                        aria-expanded={statusMenuOpen}
                    >
                        <span className={`w-1.5 h-1.5 rounded-full ${STATE_DOT[state] || STATE_DOT.active}`} />
                        {STATE_LABEL[state] || state}
                        {savingState ? <Loader2 size={11} className="animate-spin" /> : <ChevronDown size={11} strokeWidth={1.75} />}
                    </button>
                    {statusMenuOpen && (
                        <>
                            <div className="fixed inset-0 z-30" onClick={() => setStatusMenuOpen(false)} />
                            <div
                                data-testid="ops-projects-detail-state-menu"
                                className="absolute right-0 mt-1 z-40 min-w-[160px] rounded-md border border-[#E5E7EB] bg-white shadow-lg py-1"
                                role="menu"
                            >
                                {STATE_OPTIONS.map((opt) => (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => handleStateChange(opt.key)}
                                        data-testid={`ops-projects-detail-state-option-${opt.key}`}
                                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#F5F5F7] flex items-center gap-2 ${
                                            state === opt.key ? "text-[#131B2F] font-medium" : "text-[#4B5565]"
                                        }`}
                                        role="menuitem"
                                    >
                                        <span className={`w-1.5 h-1.5 rounded-full ${STATE_DOT[opt.key]}`} />
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                </div>
            </div>

            {/* UAT blocker fix · Delete confirmation dialog. Uses the
                shadcn Dialog primitive already in use elsewhere in this
                file. Requires the operator to type the project reference
                to unlock the destructive button. */}
            <Dialog
                open={deleteOpen}
                onOpenChange={(open) => {
                    setDeleteOpen(open);
                    if (!open) setDeleteConfirmText("");
                }}
            >
                <DialogContent data-testid="ops-projects-delete-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[#B91C1C]">
                            <AlertTriangle size={18} strokeWidth={2} />
                            Permanently delete project
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm text-[#131B2F]">
                        <p>
                            You are about to permanently delete{" "}
                            <span className="font-medium">{project.customer_name}</span>{" "}
                            (<span className="font-mono">{project.reference}</span>).
                        </p>
                        <div className="rounded-md border border-[#FECACA] bg-[#FEF2F2] p-3 text-xs text-[#7F1D1D]">
                            <div className="font-medium mb-1">This will cascade-remove:</div>
                            <ul className="list-disc ml-4 space-y-0.5">
                                <li>All tasks, appointments &amp; logistics</li>
                                <li>Site survey, roof elevations &amp; installation specifications</li>
                                <li>Messages &amp; notifications</li>
                                <li>Documents will be archived (not hard-deleted)</li>
                            </ul>
                            <div className="mt-2 font-medium">This action cannot be undone.</div>
                        </div>
                        <div>
                            <label className="block text-xs text-[#4B5565] mb-1">
                                Type <span className="font-mono text-[#131B2F]">{project.reference}</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                data-testid="ops-projects-delete-confirm-input"
                                className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm text-[#131B2F] outline-none focus:border-[#B91C1C]"
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button
                            type="button"
                            onClick={() => setDeleteOpen(false)}
                            disabled={deleting}
                            data-testid="ops-projects-delete-cancel"
                            className="pe-btn-outline h-10 px-4"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleDeleteProject}
                            disabled={deleting || deleteConfirmText.trim() !== (project.reference || "")}
                            data-testid="ops-projects-delete-confirm"
                            className="h-10 px-4 rounded-md bg-[#B91C1C] text-white font-medium hover:bg-[#991B1B] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                        >
                            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={2} />}
                            Permanently delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Snapshot tiles — all clickable */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {isSales ? (
                    <ProjectLeadReadOnlyCard
                        assignedEngineer={assignedEngineer}
                        testid="ops-projects-detail-engineer"
                    />
                ) : (
                    <ActionTile
                        icon={Wrench}
                        label="Project Lead"
                        value={assignedEngineer || "Assign an engineer"}
                        onClick={() => setEngineerModal(true)}
                        testid="ops-projects-detail-engineer"
                        hint={assignedEngineer ? "Click to change" : "Click to assign"}
                        emphasise={!assignedEngineer}
                    />
                )}
                <ActionTile
                    icon={Calendar}
                    label="Next visit"
                    value={
                        nextAppt
                            ? `${nextAppt.title || nextAppt.type || "Appointment"} · ${formatDate(nextAppt.scheduled_at, {
                                weekday: "short", day: "numeric", month: "short",
                                hour: "2-digit", minute: "2-digit",
                            })}`
                            : "No upcoming visit"
                    }
                    onClick={() => setVisitModal(true)}
                    testid="ops-projects-detail-next-visit"
                    hint={nextAppt ? "Click to view" : "Open calendar to review past visits or schedule a new one"}
                />
                <ActionTile
                    icon={FileText}
                    label="Documents"
                    value={`${project.documents_count || 0} on file`}
                    onClick={() => setDocsModal(true)}
                    testid="ops-projects-detail-docs"
                    hint="Click to open"
                />
                <ActionTile
                    icon={MessageSquare}
                    label="Customer messages"
                    value={
                        project.unread_customer_messages > 0
                            ? `${project.unread_customer_messages} unread`
                            : "Up to date"
                    }
                    onClick={() => setMessagesModal(true)}
                    testid="ops-projects-detail-messages"
                    tone={project.unread_customer_messages > 0 ? "amber" : "neutral"}
                    hint="Click to view thread"
                />
            </div>

            {/* Current phase / status
                WF-001A0A · The `current_owner` sentence used to say
                "Working on {phase_name}", which presented a phase as
                an activity — misleading operators. Removed. The
                truthful next-actionable step is already surfaced in
                Next milestone below. */}
            <section className="rounded-xl border border-[#E5E7EB] bg-white p-5">
                <ProjectSummaryCard project={project} />
            </section>

            {/* OPS-004B · Canonical stage summary + collapsible
                history sourced from the OPS-004A detail contract. */}
            <StageSummarySection
                project={project}
                nextActionTaskId={currentStageFocus?.task?.id || null}
                onOpenNextAction={currentStageFocus?.task ? () => handleTaskClick(currentStageFocus.task) : null}
            />
            <StageHistorySection history={project.stage_history} />

            {/* Phases — collapsible + drill-down */}
            <section className="rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-testid="ops-projects-detail-phases">
                {/* OPS-003B2 · Deep-link banner for overdue-task focus. */}
                {focusResult?.missing && (
                    <div
                        data-testid="ops-projects-detail-task-not-found"
                        className="m-4 mb-0 px-3 py-2 rounded-md border border-[#F5A524]/40 bg-[#FEF7EA] text-[#7A5B00] text-[12px]"
                    >
                        The linked task was not found on this project. Nothing has been opened —
                        the exception may point at a task that has been removed. Open the
                        related exception to review or update it.
                    </div>
                )}
                {focusResult?.task && (
                    <div
                        data-testid="ops-projects-detail-task-focused"
                        className="m-4 mb-0 px-3 py-2 rounded-md border border-[#3E9E2E]/30 bg-[#3E9E2E]/[0.08] text-[#357F26] text-[12px]"
                    >
                        Focused on <strong>{focusResult.task.name}</strong> in
                        {" "}<em>{focusResult.phase.name}</em>. Click the task to advance its
                        status.
                    </div>
                )}
                <div className="flex items-center justify-between p-5">
                    <div className="flex items-center gap-3">
                        <span aria-hidden className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-[#EEF2FF] text-[#1E3A8A] shrink-0">
                            <Wrench size={14} strokeWidth={1.75} />
                        </span>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4B5565]">
                                Phases
                            </div>
                            <div className="text-xs text-[#4B5565] mt-0.5">
                                {project.outstanding_tasks || 0} outstanding tasks · click a phase for tasks
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setPhasesCollapsed((v) => !v)}
                        data-testid="ops-projects-detail-phases-toggle"
                        aria-expanded={!phasesCollapsed}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-[#E5E7EB] bg-white text-[#131B2F] hover:bg-[#F5F5F7]"
                        title={phasesCollapsed ? "Expand" : "Collapse"}
                    >
                        {phasesCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                    </button>
                </div>
                {!phasesCollapsed && (
                    <ul className="divide-y divide-[#F0F1F5] border-t border-[#F0F1F5]">
                        {(project.phases || []).map((ph) => (
                            <PhaseBlock
                                key={ph.id || ph.name}
                                phase={ph}
                                open={openPhases.has(ph.id || ph.name)}
                                onToggle={() => togglePhase(ph.id || ph.name)}
                                onTaskClick={handleTaskClick}
                                savingTaskId={savingTask}
                                canManageProjects={canManageProjects}
                                engineersById={engineersById}
                                onAssignTask={(t) => setAssigneesDialog({
                                    task: t,
                                    currentAssigneeIds: Array.isArray(t.assignee_ids) ? t.assignee_ids : [],
                                    currentRequirement: t.required_resource_type || null,
                                })}
                                onReopenTask={(t) => setReopenDialog({ task: t })}
                            />
                        ))}
                    </ul>
                )}
            </section>

            {assigneesDialog && (
                <TaskAssigneesDialog
                    projectId={project.id}
                    task={assigneesDialog.task}
                    currentAssigneeIds={assigneesDialog.currentAssigneeIds}
                    currentRequirement={assigneesDialog.currentRequirement}
                    onClose={() => setAssigneesDialog(null)}
                    onSaved={async () => {
                        setAssigneesDialog(null);
                        await refreshProject();
                    }}
                />
            )}

            {reopenDialog && (
                <TaskReopenDialog
                    projectId={project.id}
                    task={reopenDialog.task}
                    onClose={() => setReopenDialog(null)}
                    onReopened={async () => {
                        setReopenDialog(null);
                        await refreshProject();
                    }}
                />
            )}

            {/* Customer context */}
            <section className="rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5">
                <div className="flex items-center gap-3 mb-4">
                    <span aria-hidden className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-[#EEF2FF] text-[#1E3A8A] shrink-0">
                        <User size={14} strokeWidth={1.75} />
                    </span>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4B5565]">
                        Customer
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <ContactLine icon={User} label="Name" value={project.customer_name} />
                    <ContactLine icon={Mail} label="Email" value={project.customer_email} />
                    <ContactLine
                        icon={MapPin}
                        label="Address"
                        value={project.address}
                        action={(() => {
                            const addr = resolveMapAddress({ project });
                            if (!addr) return null;
                            return (
                                <button
                                    type="button"
                                    onClick={() => openDirections(addr)}
                                    data-testid="ops-project-directions"
                                    title={`Directions to ${addr}`}
                                    className="mt-1 inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs font-medium bg-white border border-[#E5E7EB] text-[#131B2F] hover:bg-[#131B2F] hover:text-white transition-colors"
                                >
                                    <Navigation size={11} strokeWidth={2} /> Directions
                                </button>
                            );
                        })()}
                    />
                    <ContactLine icon={Clock} label={projectCompletionField(project).label} value={projectCompletionField(project).value} testid="opd-contact-completion" />
                </div>
            </section>

            {/* DEV-010A · Digital Site Survey shell — Ops-only.
                WF-001A2C · Post-mutation project refresh callback
                wired through so approve/return-for-clarification on
                the OperationsSurveyReview drawer route through the
                same shared canonical refresh as engineer assignment
                and logistics. */}
            <SiteSurveySection
                project={project}
                reviewSurveyId={reviewSurveyId}
                onProjectChanged={refreshProject}
            />

            {/* DEV-008 · Delivery & site logistics — Ops-only */}
            <LogisticsSection project={project} onProjectChanged={refreshProject} />

            {/* DEV-018 · Commercial attribution (Sales Owner + Source) */}
            <ProjectAttributionSection project={project} onProjectUpdated={onProjectUpdated} />

            {/* DEV-019 · Opportunity outcome (Won / Lost) — commercial marker only. */}
            <div id="section-opportunity-outcome">
                <OpportunityOutcomeSection project={project} onProjectUpdated={onProjectUpdated} />
            </div>

            {/* DEV-019 UAT3 · Canonical Sales lifecycle stage (Enquiry
                → Decision). Distinct from operational ``current_stage``. */}
            <div id="section-sales-stage">
                <SalesStageSection project={project} onProjectUpdated={onProjectUpdated} />
            </div>

            {/* DEV-019 · Next Sales Action + history
                DEV-019 UAT correction 2 · The `id="section-sales-actions"`
                wrapper is the deep-link target used by Focus routes
                carrying `?section=sales-actions` (dashboard Missing /
                Due today / Overdue KPIs). */}
            <div id="section-sales-actions">
                <SalesActionsSection project={project} onProjectUpdated={onProjectUpdated} />
            </div>

            {/* SYS-001 · Canonical "My System" (Ops edit; customer read-only) */}
            <MySystemSection project={project} currentUser={user} />

            {/* Activity for this project */}
            <section data-testid="ops-projects-detail-activity">
                <ActivityFeed
                    variant="operations"
                    projectId={project.id}
                    testidPrefix="ops-project-activity"
                    initialLimit={5}
                    collapsible
                />
            </section>

            {/* ─── Modals ─── */}
            {engineerModal && !isSales && (
                <EngineerAssignDialog
                    project={project}
                    onClose={() => setEngineerModal(false)}
                    onSaved={async () => {
                        // WF-001A2B · Route through the shared canonical
                        // refresh so the post-sync task/phase/outstanding
                        // state replaces our snapshot in one hop. The
                        // modal already handled the source mutation and
                        // the backend awaits WF-001A2 sync before
                        // returning — no polling / no timeouts.
                        setEngineerModal(false);
                        await refreshProject();
                    }}
                />
            )}
            {visitModal && (
                <NextVisitDialog
                    project={project}
                    onClose={() => setVisitModal(false)}
                    onSchedule={openScheduleForm}
                />
            )}
            {/* CAL-001A · Inline scheduling form for Operations. Same
                validation + clash detection as the Installer Calendar
                (both are backed by /api/calendar/appointments). */}
            {scheduleFormOpen && (
                <CalendarAppointmentForm
                    open
                    initial={scheduleInitial}
                    projects={scheduleProjectsList}
                    engineers={scheduleAssets.engineers}
                    appointmentTypes={scheduleAppointmentTypes}
                    onClose={closeScheduleForm}
                    onClashDetected={async ({ engineer_ids, start_at, end_at, ignore_appointment_id, resubmit }) => {
                        try {
                            const check = await api.checkCalendarClash({
                                engineer_ids, start_at, end_at, ignore_appointment_id,
                            });
                            setScheduleClash({
                                open: true,
                                clashes: check.clashes,
                                onResume: async () => { await resubmit(); },
                            });
                        } catch {
                            toast.error("Failed to fetch clash details");
                        }
                    }}
                    onAvailabilityWarning={({ warnings, resubmit }) => {
                        setScheduleAvail({
                            open: true,
                            warnings: warnings || [],
                            onResume: async () => { await resubmit(); },
                        });
                    }}
                />
            )}
            <ClashWarningDialog
                open={scheduleClash.open}
                clashes={scheduleClash.clashes}
                onCancel={() => setScheduleClash({ open: false, clashes: [], onResume: null })}
                onOverride={async () => {
                    const fn = scheduleClash.onResume;
                    setScheduleClash({ open: false, clashes: [], onResume: null });
                    if (fn) await fn();
                }}
            />
            <AvailabilityWarningDialog
                open={scheduleAvail.open}
                warnings={scheduleAvail.warnings}
                onCancel={() => setScheduleAvail({ open: false, warnings: [], onResume: null })}
                onOverride={async () => {
                    const fn = scheduleAvail.onResume;
                    setScheduleAvail({ open: false, warnings: [], onResume: null });
                    if (fn) await fn();
                }}
            />
            {/* WF-001A1A · Operational-override confirmation dialog.
                Driven entirely by the structured refusal payload. No
                native prompt/alert/confirm anywhere in this flow. */}
            <Dialog
                open={!!overrideDialog}
                onOpenChange={(v) => {
                    if (v) return;
                    // Escape / overlay click. Only permitted before submission.
                    if (overrideDialog?.submitting) return;
                    closeOverrideDialog();
                }}
            >
                {overrideDialog && (
                    <DialogContent
                        className="max-w-md"
                        data-testid="ops-task-override-dialog"
                    >
                        <DialogHeader>
                            <DialogTitle data-testid="ops-task-override-title">
                                Override to complete '{overrideDialog.task.name}'
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 text-sm text-[#131B2F]">
                            <div data-testid="ops-task-override-message">
                                {overrideDialog.message}
                            </div>
                            {overrideDialog.evaluation?.blocking_rules?.length > 0 && (
                                <div>
                                    <div className="text-xs font-medium text-[#4B5565] mb-1">
                                        Outstanding dependencies
                                    </div>
                                    <ul
                                        className="list-disc pl-5 space-y-0.5 text-sm"
                                        data-testid="ops-task-override-blocking-list"
                                    >
                                        {overrideDialog.evaluation.blocking_rules.map((r) => (
                                            <li key={r.task_key} data-testid={`ops-task-override-blocking-${r.task_key}`}>
                                                {r.task_name}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {overrideDialog.evaluation?.resolution_routes?.length > 0 && (
                                <div className="text-xs text-[#4B5565]" data-testid="ops-task-override-resolution">
                                    Resolve at:{" "}
                                    {overrideDialog.evaluation.resolution_routes.map((r) => (
                                        <a key={r} href={r} className="underline">{r}</a>
                                    )).reduce((acc, cur, i) => acc.length ? [...acc, ", ", cur] : [cur], [])}
                                </div>
                            )}
                            <div className="rounded-md border border-[#F5A524]/40 bg-[#FFF8EB] px-3 py-2 text-xs text-[#7A5B00]">
                                This is an <strong>operational override</strong>. Your name,
                                the reason and the affected dependency keys will be
                                recorded on the task and in the audit history.
                            </div>
                            <label htmlFor="ops-task-override-reason" className="block text-xs font-medium text-[#131B2F]">
                                Override reason (required)
                            </label>
                            <textarea
                                id="ops-task-override-reason"
                                data-testid="ops-task-override-reason"
                                rows={3}
                                value={overrideDialog.reason}
                                onChange={(e) => setOverrideDialog((s) => s
                                    ? { ...s, reason: e.target.value }
                                    : s)}
                                placeholder="Explain the operational reason for bypassing this dependency…"
                                disabled={overrideDialog.submitting}
                                className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm bg-white disabled:bg-[#F5F5F7]"
                                autoFocus
                            />
                            {overrideDialog.error && (
                                <div className="text-sm text-[#DA3535]" data-testid="ops-task-override-error">
                                    {overrideDialog.error}
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <button
                                type="button"
                                onClick={closeOverrideDialog}
                                disabled={overrideDialog.submitting}
                                data-testid="ops-task-override-cancel"
                                className="h-9 px-3 rounded-md text-xs text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmOverride}
                                disabled={
                                    overrideDialog.submitting
                                    || !overrideDialog.reason.trim()
                                }
                                data-testid="ops-task-override-confirm"
                                className="h-9 px-3 rounded-md text-xs font-medium bg-[#3E9E2E] text-white hover:opacity-90 disabled:opacity-50"
                            >
                                {overrideDialog.submitting
                                    ? "Overriding…"
                                    : "Override and complete"}
                            </button>
                        </DialogFooter>
                    </DialogContent>
                )}
            </Dialog>
            {docsModal && (
                <ProjectDocumentsDialog
                    project={project}
                    onClose={() => setDocsModal(false)}
                />
            )}
            {messagesModal && (
                <ProjectMessagesDialog
                    project={project}
                    onClose={() => setMessagesModal(false)}
                    onRead={() => patchProject({ unread_customer_messages: 0 })}
                />
            )}
        </div>
    );
}


// ─── Reusable pieces ───────────────────────────────────────────────────

function ActionTile({ icon: Icon, label, value, onClick, hint, testid, tone = "neutral", emphasise = false }) {
    const toneClass =
        tone === "amber" ? "text-[#B45309] bg-[#FFF8EB]" :
        emphasise ? "text-[#3E9E2E] bg-[#EAF6E1]" :
        "text-[#131B2F] bg-[#F5F5F7]";
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`w-full text-left rounded-xl border ${emphasise ? "border-[#3E9E2E]/40" : "border-[#E5E7EB]"} bg-white p-4 flex items-start gap-3 hover:border-[#131B2F] hover:shadow-sm transition-all group`}
        >
            <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${toneClass}`}>
                <Icon size={16} strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] font-medium">{label}</div>
                <div className="mt-0.5 text-sm text-[#131B2F] font-medium truncate">{value}</div>
                {hint && <div className="mt-0.5 text-[11px] text-[#4B5565]">{hint}</div>}
            </div>
            <ChevronRight size={14} className="text-[#D1D5DB] group-hover:text-[#131B2F] transition-colors mt-1 shrink-0" />
        </button>
    );
}

// DEV-019/020 UAT · Sales-side, read-only representation of the Project
// Lead tile. Deliberately renders as a non-interactive card (no click
// handler, no chevron, no mutation-suggesting styling) so Sales users
// see the current Project Lead for context but cannot invoke — or even
// discover — the assign / change / clear affordance. The Ops role
// continues to render the interactive `ActionTile`. UI enforcement is
// independent of the backend 403, which remains in place.
function ProjectLeadReadOnlyCard({ assignedEngineer, testid }) {
    return (
        <div
            data-testid={testid}
            data-readonly="true"
            className="w-full text-left rounded-xl border border-[#E5E7EB] bg-white p-4 flex items-start gap-3"
        >
            <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 text-[#131B2F] bg-[#F5F5F7]">
                <Wrench size={16} strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] font-medium">Project Lead</div>
                <div
                    className="mt-0.5 text-sm text-[#131B2F] font-medium truncate"
                    data-testid={`${testid}-value`}
                >
                    {assignedEngineer || "Not assigned"}
                </div>
            </div>
        </div>
    );
}

function ContactLine({ icon: Icon, label, value, action = null, testid = null }) {
    return (
        <div className="flex items-start gap-2.5" data-testid={testid || undefined}>
            <div className="w-7 h-7 rounded-md bg-[#F5F5F7] text-[#131B2F] flex items-center justify-center shrink-0">
                <Icon size={13} strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] font-medium" data-testid={testid ? `${testid}-label` : undefined}>{label}</div>
                <div className="text-sm text-[#131B2F] break-words" data-testid={testid ? `${testid}-value` : undefined}>{value || "—"}</div>
                {action}
            </div>
        </div>
    );
}

function PhaseBlock({ phase, open, onToggle, onTaskClick, savingTaskId, canManageProjects, engineersById, onAssignTask, onReopenTask }) {
    const tasks = phase.tasks || [];
    const done = tasks.filter((t) => t.status === "complete").length;
    const total = tasks.length;
    const isDone = phase.status === "complete";
    const isActive = phase.status === "in_progress";
    return (
        <li data-testid={`ops-projects-detail-phase-${phase.id || phase.name}`}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                data-testid={`ops-projects-detail-phase-row-${phase.id || phase.name}`}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-[#FAFAFC] text-left"
            >
                <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                        isDone ? "bg-[#3E9E2E]" : isActive ? "bg-[#F5A524]" : "bg-[#D1D5DB]"
                    }`}
                />
                <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#131B2F] truncate">{phase.name}</div>
                    {total > 0 && (
                        <div className="text-[11px] text-[#4B5565]">
                            {done} / {total} tasks complete
                        </div>
                    )}
                </div>
                {open ? <ChevronUp size={14} className="text-[#4B5565]" /> : <ChevronDown size={14} className="text-[#4B5565]" />}
            </button>
            {open && (
                <div className="bg-[#FAFAFC] border-t border-[#F0F1F5]" data-testid={`ops-projects-detail-phase-tasks-${phase.id || phase.name}`}>
                    {tasks.length === 0 ? (
                        <div className="px-5 py-4 text-xs text-[#4B5565]">No tasks in this phase yet.</div>
                    ) : (
                        <ul className="divide-y divide-[#F0F1F5]">
                            {tasks.map((t) => (
                                <TaskRow
                                    key={t.id}
                                    task={t}
                                    onClick={() => onTaskClick(t)}
                                    saving={savingTaskId === t.id}
                                    canManageProjects={canManageProjects}
                                    engineersById={engineersById}
                                    onAssign={() => onAssignTask(t)}
                                    onReopen={() => onReopenTask(t)}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </li>
    );
}

// TASK-ASSIGNMENT-ACTION-STATE · Exported so focused tests can render
// just the row without dragging in the AuthContext + router providers
// the full page depends on.
export function TaskRow({ task, onClick, saving, canManageProjects, engineersById = {}, onAssign, onReopen }) {
    const [expanded, setExpanded] = useState(false);
    const statusClass = task.status === "complete"
        ? "text-[#3E9E2E]"
        : task.status === "in_progress"
        ? "text-[#F5A524]"
        : "text-[#4B5565]";
    const Icon = task.status === "complete" ? CheckCircle2 : Circle;
    // Compute assignment state from STABLE IDs across every supported
    // shape (canonical `assignee_ids`, ad-hoc
    // `assigned_engineer_ids`, legacy single `assigned_engineer_id`).
    // Name/email are never used.
    const hasAssignment = (Array.isArray(task.assignee_ids) && task.assignee_ids.length > 0)
        || (Array.isArray(task.assigned_engineer_ids) && task.assigned_engineer_ids.length > 0)
        || !!task.assigned_engineer_id;
    const isLocked = task.status === "complete" || task.status === "cancelled";
    return (
        <li className="text-sm data-[focused=true]:ring-2 data-[focused=true]:ring-[#3E9E2E] data-[focused=true]:bg-[#3E9E2E]/[0.05] transition-all"
            data-testid={`ops-projects-detail-task-${task.id}`}>
            <div className="flex items-start gap-3 px-5 py-2.5">
                <button
                    type="button"
                    onClick={onClick}
                    disabled={saving || task.status === "complete"}
                    data-testid={`ops-projects-detail-task-toggle-${task.id}`}
                    aria-label={task.status === "complete"
                        ? `${task.name} is complete — use Reopen to change its status`
                        : `Mark ${task.name} ${TASK_STATUS_LABEL[TASK_STATUS_NEXT[task.status] || "in_progress"]}`}
                    title={task.status === "complete"
                        ? "Completed — use Reopen (a reason is required) to change this task"
                        : undefined}
                    className={`shrink-0 mt-0.5 ${statusClass} hover:opacity-70 disabled:opacity-40 ${task.status === "complete" ? "cursor-default" : ""}`}
                >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} strokeWidth={1.75} />}
                </button>
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid={`ops-projects-detail-task-row-${task.id}`}
                    className="flex-1 min-w-0 text-left"
                >
                    <div className={`text-sm ${task.status === "complete" ? "text-[#4B5565] line-through" : "text-[#131B2F]"} truncate`}>
                        {task.name}
                    </div>
                    <div className="text-[11px] text-[#4B5565] mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span>Status: <span className={statusClass}>{TASK_STATUS_LABEL[task.status] || task.status}</span></span>
                        {task.required_resource_type && (
                            <span data-testid={`ops-projects-detail-task-required-type-${task.id}`}>
                                · Requires: <span className="text-[#131B2F] font-medium">{task.required_resource_type}</span>
                            </span>
                        )}
                        {/* FIELD-001C · Assignee display. Historical legacy
                            rows may have no `assignee_ids` — show the neutral
                            "No assignee recorded" so operators never infer an
                            invented owner. */}
                        {Array.isArray(task.assignee_names) && task.assignee_names.length > 0 ? (
                            <span data-testid={`ops-projects-detail-task-assignees-${task.id}`}>
                                · Assigned: <span className="text-[#131B2F]">{task.assignee_names.join(", ")}</span>
                            </span>
                        ) : task.assignee_name ? (
                            <span>· Legacy assignee: <span className="text-[#131B2F]">{task.assignee_name}</span></span>
                        ) : task.status === "complete" ? (
                            <span data-testid={`ops-projects-detail-task-noassignee-${task.id}`}
                                  className="italic">
                                · No assignee recorded
                            </span>
                        ) : null}
                        {task.due_date && <span>· Due: {formatDate(task.due_date)}</span>}
                        {task.priority && <span>· Priority: {task.priority}</span>}
                    </div>
                    {/* FIELD-001B · Canonical assignees + trade */}
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5"
                         data-testid={`ops-projects-detail-task-assignees-${task.id}`}>
                        {(task.assignee_ids || []).length === 0 ? (
                            // FIELD-001C UAT · Completed rows already show a
                            // "No assignee recorded" note in the meta row —
                            // don't ALSO render an "Unassigned" chip below
                            // (Codex UAT defect: duplicate wording).
                            task.status === "complete" ? null : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F5F5F7] text-[#4B5565]"
                                      data-testid={`ops-projects-detail-task-unassigned-${task.id}`}>
                                    Unassigned
                                </span>
                            )
                        ) : (
                            (task.assignee_ids || []).map((eid) => {
                                const eng = engineersById[eid];
                                // FIELD-RESOURCE-ASSIGNMENT-CORRECTION ·
                                // Never leak raw UUIDs. Missing rows
                                // render as "Unknown or archived
                                // (id: xxx8)" so Operations sees a
                                // clear replace signal instead of a
                                // bare id. Archived rows keep their
                                // display name but carry an
                                // "Archived" badge.
                                const archived = !!eng && eng.active === false;
                                const missing = !eng;
                                const nameFallback = task.assignee_names
                                    ? task.assignee_names[(task.assignee_ids || []).indexOf(eid)]
                                    : null;
                                const label = eng?.displayName
                                    || nameFallback
                                    || (missing ? `Unknown or archived (id: ${String(eid).slice(0, 8)}…)` : eid);
                                const trade = eng?.trade || eng?.role;
                                const tone = missing || archived
                                    ? "bg-[#FEF3C7] text-[#92400E] border border-[#F5A524]/40"
                                    : "bg-[#EAF6E1] text-[#2E7E22]";
                                return (
                                    <span key={eid}
                                          data-testid={`ops-projects-detail-task-assignee-${task.id}-${eid}`}
                                          data-stale={missing || archived ? "true" : undefined}
                                          data-stale-reason={missing ? "missing" : (archived ? "archived" : undefined)}
                                          title={missing
                                              ? `Unknown or archived resource (id: ${eid}). Reassign to remove.`
                                              : archived ? `${eng.displayName} is archived — reassign to remove.` : undefined}
                                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${tone}`}>
                                        <span
                                            className="w-3 h-3 rounded-full inline-block"
                                            style={{ background: (missing || archived) ? "#F59E0B" : (eng?.colour || "#3E9E2E") }}
                                        />
                                        {label}{trade && !missing ? ` · ${trade}` : ""}
                                        {archived && (
                                            <span data-testid={`ops-projects-detail-task-assignee-archived-${task.id}-${eid}`}
                                                  className="ml-1 px-1 rounded bg-white/50">
                                                Archived
                                            </span>
                                        )}
                                    </span>
                                );
                            })
                        )}
                    </div>
                </button>
                {canManageProjects && !isLocked && (
                    <button
                        type="button"
                        onClick={onAssign}
                        data-testid={`ops-projects-detail-task-assign-${task.id}`}
                        data-assignment-state={hasAssignment ? "reassign" : "assign"}
                        aria-label={`${hasAssignment ? "Reassign" : "Assign"} ${task.name}`}
                        title={hasAssignment
                            ? "Add, remove or replace assigned resources"
                            : "Assign resources to this task"}
                        className="shrink-0 mt-0.5 text-[11px] font-medium text-[#3E9E2E] hover:text-[#348426] px-2 py-0.5 rounded border border-[#3E9E2E]/40 hover:bg-[#EAF6E1] transition-colors"
                    >
                        {hasAssignment ? "Reassign" : "Assign"}
                    </button>
                )}
                {canManageProjects && task.status === "complete" && (
                    <button
                        type="button"
                        onClick={onReopen}
                        data-testid={`ops-projects-detail-task-reopen-${task.id}`}
                        aria-label={`Reopen ${task.name}`}
                        title="Reopen this task (Operations only)"
                        className="shrink-0 mt-0.5 text-[11px] font-medium text-[#131B2F] hover:text-white hover:bg-[#131B2F] px-2 py-0.5 rounded border border-[#131B2F]/50 transition-colors"
                    >
                        Reopen
                    </button>
                )}
                {canManageProjects && isLocked && hasAssignment && (
                    <span
                        data-testid={`ops-projects-detail-task-assigned-locked-${task.id}`}
                        title={task.status === "complete"
                            ? "Completed — reopen this task to change its assignees"
                            : "Cancelled — reactivate this task to change its assignees"}
                        className="shrink-0 mt-0.5 text-[11px] font-medium text-[#4B5565] px-2 py-0.5 rounded border border-[#D1D5DB] bg-[#F5F5F7] cursor-not-allowed"
                    >
                        Assigned
                    </span>
                )}
                <ChevronRight
                    size={12}
                    className={`shrink-0 mt-1 text-[#D1D5DB] transition-transform ${expanded ? "rotate-90" : ""}`}
                />
            </div>
            {expanded && (
                <div className="px-5 pb-3 pl-14 text-[11px] text-[#4B5565] space-y-1" data-testid={`ops-projects-detail-task-detail-${task.id}`}>
                    {task.description && <div>{task.description}</div>}
                    {task.completed_at && <div>Completed: {formatDate(task.completed_at, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>}
                    {task.status === "complete" ? (
                        <div className="text-[#4B5565]" data-testid={`ops-projects-detail-task-complete-hint-${task.id}`}>
                            This task is complete. Use <span className="text-[#131B2F] font-medium">Reopen</span> to change its status — a reason is required.
                        </div>
                    ) : (
                        <div className="text-[#131B2F]">
                            Tap the circle on the left to advance the status
                            (Not started → In progress → Complete).
                        </div>
                    )}
                </div>
            )}
        </li>
    );
}


// ─── Engineer assignment dialog ────────────────────────────────────────

function EngineerAssignDialog({ project, onClose, onSaved }) {
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [engineers, setEngineers] = useState([]);
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const rows = await api.listEngineers({ q, includeArchived: false });
                setEngineers(rows || []);
            } catch (e) {
                toast.error(formatApiError(e?.response?.data?.detail) || "Could not load engineers");
            } finally {
                setLoading(false);
            }
        })();
    }, [q]);

    const currentId = project.lead_engineer_id || null;

    const assign = async (engineerId) => {
        setSaving(engineerId || "clear");
        try {
            const updated = await api.assignLeadEngineer(project.id, engineerId);
            // DEV-003B: use the authoritative response so the parent
            // workspace immediately reflects the change. `assignLeadEngineer`
            // returns `serialize_project(project)` which contains the new
            // (or cleared) `lead_engineer_*` fields.
            const patch = {
                lead_engineer_id: updated.lead_engineer_id ?? null,
                lead_engineer_name: updated.lead_engineer_name ?? null,
                lead_engineer_colour: updated.lead_engineer_colour ?? null,
                lead_engineer_role: updated.lead_engineer_role ?? null,
                lead_engineer_initials: updated.lead_engineer_initials ?? null,
            };
            toast.success(engineerId ? "Project lead assigned" : "Project lead cleared");
            onSaved(patch);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not save assignment");
        } finally {
            setSaving(null);
        }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="ops-projects-engineer-modal">
                <DialogHeader>
                    <DialogTitle>Assign project lead</DialogTitle>
                </DialogHeader>
                <div className="relative mt-1">
                    <Search size={14} strokeWidth={1.75} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5565]" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search engineers"
                        data-testid="ops-projects-engineer-search"
                        className="w-full h-9 pl-9 pr-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] focus:outline-none focus:ring-2 focus:ring-[#131B2F]/10 focus:border-[#131B2F]"
                    />
                </div>
                <div
                    className="mt-2 max-h-[60vh] sm:max-h-[420px] overflow-y-auto overscroll-contain border border-[#E5E7EB] rounded-md divide-y divide-[#F0F1F5]"
                    style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
                    data-testid="ops-projects-engineer-list"
                >
                    {loading ? (
                        <div className="p-6 text-center text-xs text-[#4B5565]">
                            <Loader2 size={14} className="inline animate-spin" /> Loading…
                        </div>
                    ) : engineers.length === 0 ? (
                        <div className="p-6 text-center text-xs text-[#4B5565]">No engineers match your search.</div>
                    ) : (
                        engineers.map((eng) => {
                            const isCurrent = eng.id === currentId;
                            return (
                                <button
                                    key={eng.id}
                                    type="button"
                                    onClick={() => !isCurrent && assign(eng.id)}
                                    disabled={isCurrent || saving}
                                    data-testid={`ops-projects-engineer-option-${eng.id}`}
                                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${
                                        isCurrent ? "bg-[#EAF6E1]" : "hover:bg-[#F5F5F7]"
                                    } disabled:opacity-70`}
                                >
                                    <div
                                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0"
                                        style={{ background: eng.colour || "#131B2F" }}
                                    >
                                        {eng.initials || (eng.displayName || "?").slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-[#131B2F] font-medium truncate">
                                            {eng.displayName || eng.name}
                                        </div>
                                        <div className="text-[11px] text-[#4B5565] truncate">{eng.role || eng.email || ""}</div>
                                    </div>
                                    {isCurrent && <span className="text-[11px] text-[#3E9E2E] font-medium">Assigned</span>}
                                    {saving === eng.id && <Loader2 size={14} className="animate-spin" />}
                                </button>
                            );
                        })
                    )}
                </div>
                <DialogFooter className="flex flex-row justify-between items-center gap-2 sm:justify-between">
                    {currentId ? (
                        <button
                            type="button"
                            onClick={() => assign(null)}
                            disabled={saving === "clear"}
                            data-testid="ops-projects-engineer-clear"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#DA3535] hover:bg-[#FEE9E9] disabled:opacity-60"
                        >
                            {saving === "clear" && <Loader2 size={12} className="animate-spin" />}
                            Clear assignment
                        </button>
                    ) : <div />}
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="ops-projects-engineer-close"
                        className="h-9 px-3 rounded-md text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Close
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─── Next Visit dialog ─────────────────────────────────────────────────

function NextVisitDialog({ project, onClose, onSchedule }) {
    const appt = project.next_appointment;
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="ops-projects-visit-modal">
                <DialogHeader>
                    <DialogTitle>Next visit</DialogTitle>
                </DialogHeader>
                {appt ? (
                    <div className="space-y-3">
                        <Row label="Title" value={appt.title || appt.type || "Appointment"} />
                        <Row label="When" value={formatDate(appt.scheduled_at, {
                            weekday: "long", day: "numeric", month: "short", year: "numeric",
                            hour: "2-digit", minute: "2-digit",
                        })} />
                        <Row label="Status" value={appt.status || "scheduled"} />
                        {appt.location && <Row label="Location" value={appt.location} />}
                        {appt.engineer_name && <Row label="Engineer" value={appt.engineer_name} />}
                        {appt.customer_instructions && <Row label="Customer notes" value={appt.customer_instructions} />}
                        <div className="pt-2 flex items-center justify-between gap-2">
                            <Link
                                to="/operations/calendar"
                                data-testid="ops-projects-visit-open-calendar"
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                            >
                                <Calendar size={12} strokeWidth={1.75} /> Open calendar
                            </Link>
                            <button
                                type="button"
                                onClick={onClose}
                                className="h-9 px-3 rounded-md text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="text-sm text-[#4B5565]">
                            {/* VISIT-TASK-001D · Wording accurately
                                distinguishes "no upcoming appointment"
                                from "no appointment at all". Past
                                visits remain accessible via the
                                Open calendar link below. */}
                            No upcoming visit is scheduled for this project. Past visits remain available in the calendar.
                        </div>
                        <div className="pt-2 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={onSchedule}
                                    data-testid="ops-projects-visit-schedule"
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#3E9E2E] text-white text-xs font-medium hover:opacity-90"
                                >
                                    <Plus size={12} strokeWidth={2} /> Schedule visit
                                </button>
                                <Link
                                    to={`/operations/calendar?project=${project.id}`}
                                    data-testid="ops-projects-visit-open-calendar-empty"
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                                >
                                    <Calendar size={12} strokeWidth={1.75} /> Open calendar
                                </Link>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="h-9 px-3 rounded-md text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-start gap-3">
            <div className="w-24 text-[11px] uppercase tracking-[0.12em] text-[#4B5565] font-medium shrink-0 pt-0.5">{label}</div>
            <div className="text-sm text-[#131B2F] break-words">{value}</div>
        </div>
    );
}


// ─── Project Documents dialog ──────────────────────────────────────────

function ProjectDocumentsDialog({ project, onClose }) {
    const [loading, setLoading] = useState(true);
    const [docs, setDocs] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const rows = await api.listDocuments(project.id);
                setDocs(rows || []);
            } catch (e) {
                toast.error(formatApiError(e?.response?.data?.detail) || "Could not load documents");
            } finally {
                setLoading(false);
            }
        })();
    }, [project.id]);

    const openDoc = async (doc) => {
        try {
            const { token } = await api.documentDownloadToken(doc.id);
            window.open(`${api.documentDownloadUrl(doc.id)}?dt=${encodeURIComponent(token)}`, "_blank");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not open document");
        }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="ops-projects-docs-modal">
                <DialogHeader>
                    <DialogTitle>Documents · {project.customer_name}</DialogTitle>
                </DialogHeader>
                <div
                    className="max-h-[60vh] sm:max-h-[420px] overflow-y-auto overscroll-contain border border-[#E5E7EB] rounded-md divide-y divide-[#F0F1F5]"
                    style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
                    data-testid="ops-projects-docs-list"
                >
                    {loading ? (
                        <div className="p-6 text-center text-xs text-[#4B5565]">
                            <Loader2 size={14} className="inline animate-spin" /> Loading…
                        </div>
                    ) : docs.length === 0 ? (
                        <div className="p-6 text-center text-xs text-[#4B5565]">No documents on file for this project yet.</div>
                    ) : (
                        docs.map((d) => (
                            <button
                                key={d.id}
                                type="button"
                                onClick={() => openDoc(d)}
                                data-testid={`ops-projects-docs-item-${d.id}`}
                                className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#F5F5F7]"
                            >
                                <div className="w-8 h-8 rounded-md bg-[#F5F5F7] text-[#131B2F] flex items-center justify-center shrink-0">
                                    <FileText size={14} strokeWidth={1.75} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-[#131B2F] font-medium truncate">{d.title || d.filename}</div>
                                    <div className="text-[11px] text-[#4B5565] truncate">
                                        {d.category || "Document"}
                                        {d.uploaded_at && ` · ${formatDate(d.uploaded_at)}`}
                                    </div>
                                </div>
                                <Download size={14} className="text-[#4B5565] shrink-0" />
                            </button>
                        ))
                    )}
                </div>
                <DialogFooter>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 px-3 rounded-md text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Close
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─── Project Messages dialog ───────────────────────────────────────────

function ProjectMessagesDialog({ project, onClose, onRead }) {
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const hadUnread = (project?.unread_customer_messages || 0) > 0;
    const listRef = React.useRef(null);

    const load = useCallback(async () => {
        try {
            const rows = await api.listMessages(project.id);
            setMessages(rows || []);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not load messages");
        } finally {
            setLoading(false);
        }
    }, [project.id]);

    useEffect(() => { load(); }, [load]);

    // DEV-003C: keep the conversation pinned to the newest message on
    // open and after every send/refresh, otherwise users type + send
    // and see no feedback because the list stayed scrolled to the top.
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        // Defer to next tick so the newly appended row is measured.
        requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
        });
    }, [messages, loading]);

    // DEV-003B: acknowledge the thread as read once the messages have
    // been rendered. Uses the new `POST /projects/{id}/messages/read`
    // endpoint so the unread counter in `/projects/summary` reflects
    // the ack across refreshes.
    useEffect(() => {
        if (loading || !hadUnread) return;
        let cancelled = false;
        (async () => {
            try {
                await api.markProjectMessagesRead(project.id);
                if (!cancelled && onRead) onRead();
            } catch (e) {
                /* non-fatal — surface via toast only if the user cares */
            }
        })();
        return () => { cancelled = true; };
    }, [loading, hadUnread, project.id, onRead]);

    const send = async () => {
        const t = text.trim();
        if (!t || sending) return;
        setSending(true);
        try {
            const saved = await api.sendMessage(project.id, t);
            setMessages((prev) => [...prev, saved]);
            setText("");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not send message");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="ops-projects-messages-modal">
                <DialogHeader>
                    <DialogTitle>Messages · {project.customer_name}</DialogTitle>
                </DialogHeader>
                <div className="h-[360px] border border-[#E5E7EB] rounded-md flex flex-col">
                    <div
                        ref={listRef}
                        className="flex-1 overflow-y-auto overscroll-contain p-3 space-y-2"
                        style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
                        data-testid="ops-projects-messages-list"
                    >
                        {loading ? (
                            <div className="p-6 text-center text-xs text-[#4B5565]">
                                <Loader2 size={14} className="inline animate-spin" /> Loading…
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="p-6 text-center text-xs text-[#4B5565]">No messages yet — start the conversation below.</div>
                        ) : (
                            messages.map((m) => (
                                <div
                                    key={m.id}
                                    data-testid={`ops-projects-messages-item-${m.id}`}
                                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                                        m.sender_role === "customer"
                                            ? "bg-[#F5F5F7] text-[#131B2F]"
                                            : "bg-[#131B2F] text-white ml-auto"
                                    }`}
                                >
                                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                                    <div className={`mt-1 text-[10px] ${m.sender_role === "customer" ? "text-[#4B5565]" : "text-white/60"}`}>
                                        {m.sender_role === "customer" ? "Customer" : "You"} · {formatDate(m.created_at, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="border-t border-[#E5E7EB] p-2 flex items-center gap-2">
                        <input
                            type="text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && send()}
                            placeholder="Reply to the customer…"
                            data-testid="ops-projects-messages-input"
                            className="flex-1 h-9 px-3 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#131B2F]/10 focus:border-[#131B2F]"
                        />
                        <button
                            type="button"
                            onClick={send}
                            disabled={!text.trim() || sending}
                            data-testid="ops-projects-messages-send"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#131B2F] text-white text-xs font-medium hover:opacity-90 disabled:opacity-60"
                        >
                            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                            Send
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
